<template>
  <v-card class="my-0 px-7">
    <slot name="list-header" />
    <v-data-table
      v-if="listData && listData.length"
      id="valk-table"
      :value="selectedRows"
      :headers="headers"
      :items="listData"
      :disable-pagination="true"
      hide-default-footer
      :item-key="itemKey"
      :item-class="itemClass"
      :show-select="showSelect"
      :mobile-breakpoint="mobileBreakPt"
      :server-items-length.sync="pagination.total"
      :options.sync="listOptions"
      :header-props="{
        'sort-icon': '$faArrowUp'
      }"
      :disable-sort="!sortable"
      :sort-by="sortBy"
      @input="(e) => $emit('check-item',e)"
      @update:sort-desc="updateSort"
      @click:row="rowSelected"
    >
      <!-- eslint-disable vue/valid-v-slot -->
      <template #item.nameAndId="{ item }">
        {{ item.name }}
        <br>
        <span class="text--disabled text-caption">{{ item.id }}</span>
      </template>
      <template
        v-for="(slot, name) in $scopedSlots"
        v-slot:[name]="item"
      >
        <slot
          :name="name"
          v-bind="item"
        />
      </template>
    </v-data-table>
    <div v-else-if="isLoading">
      <v-skeleton-loader
        type="table-thead"
        loading
      />
      <v-skeleton-loader
        :type="`table-row-divider@${pagination.perPage === -1 ? '20' : pagination.perPage}`"
        loading
      />
    </div>
    <slot
      v-else
      name="empty-state"
    />
    <div
      class="d-flex justify-space-between pt-5"
      style="min-height: 70px;"
    >
      <span v-if="paginationText && !paginationText.includes('undefined')">{{ paginationText }}</span>
      <div v-else />
      <div
        class="d-flex justify-space-between mt-0 mb-3"
        style="width:420px; margin:0 0 15px 0 ; align-items:center; height:20px;"
      >
        <div
          class="d-flex justify-space-between align-center"
          style="width:100px"
        >
          <span style="letter-spacing: 0.5px; ; margin-top:2px">Page:</span>
          <v-select
            :value="pagination.pageNum"
            class="list-select"
            :append-icon="icon"
            hide-details
            style="width:40px; max-width:55px;"
            dense
            :items="pageNumberArray"
            @change="selectPageNum"
          />
        </div>
        <div
          class="d-flex justify-space-between align-center"
          style="width:165px"
        >
          <span style="letter-spacing: 0.5px; margin-top:2px">Rows per page:</span>
          <v-select
            :value="pagination.perPage"
            class="list-select"
            :append-icon="icon"
            style="max-width:65px; min-width:55px"
            hide-details
            dense
            :items="[10, 50, 100, 500, 1000]"
            @change="selectPerPage"
          />
        </div>
        <div style="width:120px">
          <v-btn
            text
            x-small
            :disabled="pagination.pageNum === 1"
            max-width="15px"
            min-width="15px"
            class="ml-2"
            @click="handleDoubleArrow('start')"
          >
            <v-icon small>
              $faChevronDoubleLeft
            </v-icon>
          </v-btn>
          <v-btn
            class="ml-2"
            max-width="15px"
            min-width="15px"
            :disabled="pagination.pageNum === 1"
            text
            x-small
            @click="handleSingleArrow('back')"
          >
            <v-icon
              small
            >
              $faChevronLeftLight
            </v-icon>
          </v-btn>
          <v-btn
            class="ml-2"
            max-width="15px"
            min-width="15px"
            text
            x-small
            :disabled="pagination.pageNum === pageNumberArray.length"
            @click="handleSingleArrow('forward')"
          >
            <v-icon small>
              $faChevronRightLight
            </v-icon>
          </v-btn>
          <v-btn
            class="ml-2"
            max-width="15px"
            min-width="15px"
            text
            x-small
            :disabled="pagination.pageNum === pageNumberArray.length"
            @click="handleDoubleArrow('end')"
          >
            <v-icon small>
              $faChevronDoubleRight
            </v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>
<script>

  export default {
    name: 'ValkyrieList',
    props: {
      listData: {
        type: Array,
        default: () => [],
      },
      pagination: {
        type: Object,
        default: () => {},
      },
      paginationText: {
        type: String,
      },
      listName: {
        type: String,
        default: 'Organizations',
      },
      headers: {
        type: Array,
        default: () => [],
      },
      sortable: {
        type: Boolean,
        default: false,
      },
      sortBy: {
        type: String,
        default: '',
      },
      showSelect: {
        type: Boolean,
        default: false,
      },
      selectedRows: {
        type: Array,
        default: () => [],
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      rowsSelectable: {
        type: [Boolean, Function],
        default: false,
      },
      itemKey: {
        type: String,
        default: '',
      },
    },
    data: () => ({
      listOptions: {},
      mobileBreakPt: 0,
      paginationOptions: [10, 50, 100, 500, 1000],
      icon: '$faChevronDown',
    }),
    computed: {
      pageNumberArray () {
        const pageArray = []
        const totalPages = Math.ceil(this.pagination.total / this.pagination.perPage)
        for (let i = 1; i <= totalPages; i++) {
          pageArray.push(i)
        }
        return pageArray
      },
    },
    mounted () {
      // the lists are all different enough from each other that they're getting their own breakpoints
      if (this.listName === 'Properties') {
        this.mobileBreakPt = '700'
      } else if (this.listName === 'Organizations' || this.listName === 'Users') {
        this.mobileBreakPt = '880'
      } else if (this.listName === 'Sensors') {
        this.mobileBreakPt = '830'
      } else if (this.listName === 'Events') {
        this.mobileBreakPt = '875'
      }
    },
    methods: {
      itemClass (item) {
        const clickable = (typeof this.rowsSelectable === 'boolean' && this.rowsSelectable) ||
          (typeof this.rowsSelectable === 'function' && this.rowsSelectable(item))
        if (clickable) return 'clickable'
        return ''
      },
      handleDoubleArrow (direction) {
        const pageNum = direction === 'end' ? this.pageNumberArray.length : 1
        this.updatePagination({ pageNum })
      },
      handleSingleArrow (direction) {
        const pageNum = this.pagination.pageNum + (direction === 'forward' ? 1 : -1)
        this.updatePagination({ pageNum })
      },
      selectPageNum (pageNum) {
        this.updatePagination({ pageNum })
      },
      selectPerPage (perPage) {
        this.updatePagination({ perPage })
      },
      updatePagination ({ perPage, pageNum } = this.pagination) {
        this.$emit('update-pagination', { perPage, pageNum })
      },
      updateSort () {
        this.$emit('update-sort', this.listOptions)
      },
      rowSelected (item) {
        if (typeof this.rowsSelectable === 'function' && !this.rowsSelectable(item)) return
        this.$emit('select-row', item)
      },
    },
  }
</script>
<style lang="scss">
#valk-table {
 border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 2px solid rgba(151,151,151,.4);
}
.v-list-item--dense, .v-list--dense .v-list-item {
  min-height:30px;
}
.clickable {
  cursor: pointer;
}
.text-start {
  white-space: pre-wrap;
}
.v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > td,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
  font-size: 16px !important;
  color: #535353 !important;
  height: 60px !important;
}
thead > tr > th > .v-data-table__checkbox > .v-icon > svg[data-icon="check-square"],
tbody > tr > td > .v-data-table__checkbox > .v-icon > svg[data-icon="check-square"]{
  color: $success !important;
}
tbody tr:hover { background: transparent !important }
.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: #9E9E9E !important;
  font-weight: bold !important;
  line-height: 22px !important;
}
.sortable .fa-arrow-up {
  height: 12px !important;
  color: #2B394E;
}
.int-disabled-tooltip {
  margin-right:100px;
  opacity: 1 !important;
}
.list-select.v-text-field > .v-input__control > .v-input__slot:before {
  border-style:none !important;
}
.list-select.v-text-field>.v-input__control>.v-input__slot:after {
  border-style:none !important;
}
.v-text-field.v-input--dense:not(.v-text-field--enclosed):not(.v-text-field--full-width) .v-input__prepend-inner .v-input__icon > .v-icon, .v-text-field.v-input--dense:not(.v-text-field--enclosed):not(.v-text-field--full-width) .v-input__append-inner .v-input__icon > .v-icon {
  margin-top: 7px !important;
}
.v-select.v-input--dense .v-select__selection--comma {
  font-size:14px;
  letter-spacing: 0.5px;
  margin: auto 0 !important;
}
.v-input__append-inner {
  margin-top:2px;
}
.v-skeleton-loader__chip {
  height: 10px !important;
}
.v-icon.notranslate.v-data-table-header__icon.theme--light {
  padding-bottom:4px;
  margin-left: 10px;
}
</style>
